import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Modal } from 'react-bootstrap';
import { Users, Eye } from 'lucide-react';
import { toast } from 'react-toastify';
import { apiRequests } from '../../shared/api/apiRequests';
import { Pagination } from '../../features/Pagination';
import { PageBar } from '../../shared/ui/PageBar';
import { EntityCard } from '../../shared/ui/EntityCard';

const WithdrawalRequests = () => {
  const [requests, setRequests] = useState({ items: [], totalCount: 0 });
  const [searchValue, setSearchValue] = useState('');
  const [filterValues, setFilterValues] = useState({ status: null });
  const [loading, setLoading] = useState(false);

  // Модалка для просмотра/редактирования заявки
  const [showModal, setShowModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [newStatus, setNewStatus] = useState(null);
  const [comment, setComment] = useState('');

  const statusOptions = [
    { value: null, label: 'Все заявки' },
    { value: 1, label: 'Ожидание' },
    { value: 2, label: 'Выплачено' },
    { value: 3, label: 'Отменено' },
  ];

  const getStatusBadge = (status) => {
    switch (status) {
      case 1:
        return <Badge bg="warning">Ожидание</Badge>;
      case 2:
        return <Badge bg="success">Выплачено</Badge>;
      case 3:
        return <Badge bg="danger">Отменено</Badge>;
      default:
        return <Badge bg="secondary">Неизвестно</Badge>;
    }
  };

  const getStatusLabel = (status) => {
    const option = statusOptions.find((opt) => opt.value === status);
    return option ? option.label : 'Неизвестно';
  };

  const getData = async (count = 20, offset = 0) => {
    try {
      setLoading(true);
      const response = await apiRequests.referralProgram.getWithdrawalRequests(
        filterValues.status,
        null,
        searchValue,
        count,
        offset,
      );
      setRequests(response);
    } catch (e) {
      console.error('Ошибка загрузки заявок:', e);
      toast.error('Ошибка загрузки заявок');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    getData();
  };

  const handleFilterChange = (key, value) => {
    if (key === 'status') {
      const intValue = value === null ? null : parseInt(value);
      setFilterValues((prev) => ({ ...prev, [key]: intValue }));
    } else {
      setFilterValues((prev) => ({ ...prev, [key]: value }));
    }
  };

  const applyFilters = () => {
    getData();
  };

  const resetFilters = () => {
    setFilterValues({ status: null });
    setSearchValue('');
    getData();
  };

  const handleOpenModal = async (request) => {
    try {
      const fullRequest = await apiRequests.referralProgram.getWithdrawalRequest(request.id);
      setSelectedRequest(fullRequest);
      setNewStatus(fullRequest.status);
      setComment(fullRequest.comment || '');
      setShowModal(true);
    } catch (e) {
      console.error('Ошибка загрузки заявки:', e);
      toast.error('Ошибка загрузки заявки');
    }
  };

  const handleUpdateRequest = async () => {
    if (!selectedRequest) return;

    try {
      await apiRequests.referralProgram.updateWithdrawalRequest({
        id: selectedRequest.id,
        status: newStatus,
        comment: comment,
      });
      toast.success('Заявка обновлена');
      setShowModal(false);
      getData();
    } catch (e) {
      console.error('Ошибка обновления заявки:', e);
      toast.error(e.response?.data?.error || 'Ошибка обновления заявки');
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'Не указано';
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (value, currencySymbol = '$') => {
    return `${value.toFixed(2)} ${currencySymbol}`;
  };

  const filters = [
    {
      key: 'status',
      type: 'select',
      label: 'Статус',
      value: filterValues.status === null ? null : String(filterValues.status),
      placeholder: 'Все заявки',
      options: statusOptions.filter((o) => o.value !== null).map((o) => ({ value: String(o.value), label: o.label })),
    },
  ];

  return (
    <>
      <PageBar
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onSearch={handleSearch}
        searchPlaceholder="Поиск по комментарию..."
        filters={filters}
        onFilterChange={handleFilterChange}
        onApplyFilters={applyFilters}
        onResetFilters={resetFilters}
        rightContent={
          <Link to="/referral-program/members" className="page-bar-clean__link-btn">
            <Users size={18} />
            <span>Участники</span>
          </Link>
        }
      />

      <div className="mb-3">
        {loading ? (
          <p className="text-center">Загрузка...</p>
        ) : requests.items?.length === 0 ? (
          <p className="text-center">По вашему запросу ничего не найдено</p>
        ) : (
          requests.items?.map((request) => {
            const statusVariant = request.status === 1 ? 'warning' : request.status === 2 ? 'success' : 'danger';
            return (
              <EntityCard
                key={request.id}
                title={`Заявка #${request.id}`}
                subtitle={`Счет: ${request.accountFrom?.currency?.title || 'Не указан'}`}
                badge={{ text: getStatusLabel(request.status), variant: statusVariant }}
                fields={[
                  { label: 'Сумма', value: formatCurrency(request.transaction?.sum || 0, request.accountFrom?.currency?.symbol) },
                  { label: 'Дата', value: formatDate(request.createdAt) },
                ]}
                actions={
                  <button
                    onClick={() => handleOpenModal(request)}
                    className="entity-card__action"
                    title="Просмотр и редактирование"
                  >
                    <Eye size={20} />
                  </button>
                }
              />
            );
          })
        )}
      </div>

      {requests.totalCount > 20 && (
        <Pagination
          count={requests.totalCount}
          itemsPerPage={20}
          onChange={(page) => getData(20, 20 * page)}
        />
      )}

      {/* Модалка просмотра/редактирования заявки */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Заявка на выплату #{selectedRequest?.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest && (
            <>
              <div className="mb-3">
                <strong>Текущий статус:</strong> {getStatusBadge(selectedRequest.status)}
              </div>

              <div className="mb-3">
                <strong>Счет:</strong> {selectedRequest.accountFrom?.currency?.title} (
                {selectedRequest.accountFrom?.currency?.symbol})
              </div>

              <div className="mb-3">
                <strong>Сумма:</strong>{' '}
                {formatCurrency(
                  selectedRequest.transaction?.sum || 0,
                  selectedRequest.accountFrom?.currency?.symbol,
                )}
              </div>

              <div className="mb-3">
                <strong>Дата создания:</strong> {formatDate(selectedRequest.createdAt)}
              </div>

              {selectedRequest.transaction?.description && (
                <div className="mb-3">
                  <strong>Описание транзакции:</strong>{' '}
                  {selectedRequest.transaction.description}
                </div>
              )}

              <hr />

              {selectedRequest.status === 1 && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Изменить статус</Form.Label>
                    <Form.Select
                      value={newStatus}
                      onChange={(e) => setNewStatus(parseInt(e.target.value))}
                    >
                      {statusOptions
                        .filter((opt) => opt.value !== null)
                        .map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Комментарий</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Добавьте комментарий к заявке"
                    />
                  </Form.Group>
                </>
              )}

              {selectedRequest.status !== 1 && (
                <div className="alert alert-info">
                  Заявка уже обработана. Редактирование невозможно.
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Закрыть
          </Button>
          {selectedRequest?.status === 1 && (
            <Button variant="primary" onClick={handleUpdateRequest}>
              Сохранить изменения
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default WithdrawalRequests;
